import { useState, type FormEvent, type ReactNode } from 'react';
import { describeError } from '../../api/client';
import styles from './admin.module.css';

/**
 * Shared pieces of the organisation admin area (Mission 018).
 *
 * ---------------------------------------------------------------------
 * WHY ACTIONS ARE NAMED SEPARATELY
 *
 * Create, edit, retire and deprecate are four different acts with four
 * different consequences, and the schema treats them as such. This file
 * gives each its own control and its own wording, because a single "save"
 * button that sometimes retires something is how an administrator ends up
 * doing one thing while believing they did another.
 *
 * The word **delete** appears nowhere in this area, and there is no control
 * that could produce one. Nothing in this product is deleted: every
 * definition table forbids it at the database level, and a button offering
 * it would be a lie about what the system can do.
 * ---------------------------------------------------------------------
 */

export type ActionState =
  | { status: 'idle' }
  | { status: 'saving' }
  | { status: 'failed'; message: string }
  | { status: 'done'; message: string };

/** Runs a mutation, reporting exactly what the server said.
 *
 * A failure is surfaced, never swallowed: `docs/FRONTEND.md` §5d's rule
 * that a refused write must be visible applies with more force here,
 * because the refusals in this area are the product's own integrity rules
 * explaining themselves. */
export function useAction(): {
  state: ActionState;
  run: (work: () => Promise<string>) => Promise<boolean>;
  reset: () => void;
} {
  const [state, setState] = useState<ActionState>({ status: 'idle' });

  async function run(work: () => Promise<string>): Promise<boolean> {
    setState({ status: 'saving' });
    try {
      const message = await work();
      setState({ status: 'done', message });
      return true;
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
      return false;
    }
  }

  return { state, run, reset: () => setState({ status: 'idle' }) };
}

export function ActionFeedback({ state }: { state: ActionState }) {
  if (state.status === 'failed') {
    return (
      <p className={styles.failure} role="alert">
        {state.message}
      </p>
    );
  }
  if (state.status === 'done') {
    return (
      <p className={styles.success} role="status">
        {state.message}
      </p>
    );
  }
  return null;
}

/** A labelled text input. Labels are real `<label for>` associations
 * rather than placeholders, so the field is announced when focused. */
export function Field({
  id,
  label,
  value,
  onChange,
  hint,
  multiline,
  disabled,
  disabledReason,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  multiline?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const describedBy = hint || disabledReason ? `${id}-hint` : undefined;
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          className={styles.input}
          rows={3}
          value={value}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          className={styles.input}
          type="text"
          value={value}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {describedBy ? (
        <p className={styles.hint} id={describedBy}>
          {disabledReason ?? hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A consequential action behind an explicit confirmation.
 *
 * Used for retirement and deprecation. The confirmation states what the
 * action actually does — including what it does *not* do — because the
 * single most likely misunderstanding in this area is that retiring
 * something removes it.
 */
export function ConfirmAction({
  label,
  confirmLabel,
  description,
  onConfirm,
  busy,
}: {
  label: string;
  confirmLabel: string;
  description: ReactNode;
  onConfirm: () => void;
  busy: boolean;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button type="button" className={styles.secondaryButton} onClick={() => setOpen(true)}>
        {label}
      </button>
    );
  }

  return (
    <div className={styles.confirm}>
      <p className={styles.confirmText}>{description}</p>
      <div className={styles.actions}>
        <button type="button" className={styles.dangerButton} onClick={onConfirm} disabled={busy}>
          {busy ? 'Working…' : confirmLabel}
        </button>
        <button type="button" className={styles.secondaryButton} onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function AdminForm({
  title,
  onSubmit,
  submitLabel,
  busy,
  children,
}: {
  title: string;
  onSubmit: () => void;
  submitLabel: string;
  busy: boolean;
  children: ReactNode;
}) {
  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit();
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} aria-label={title}>
      <h3 className={styles.formTitle}>{title}</h3>
      {children}
      <div className={styles.actions}>
        <button type="submit" className={styles.primaryButton} disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

/**
 * The non-`active` states of every admin-editable thing, said plainly —
 * but in one word, not two. Structural entities (Role/Department/Team)
 * use the `active_status` enum (`retired`); library definitions
 * (Attribute/Skill/Qualification/Experience Area) use the separate
 * `definition_status` enum (`deprecated`) — a real, intentional schema
 * distinction (migration 0002) that every editor's own action button
 * already papers over for the user ("Retire this skill", "Retire this
 * role" — identical copy regardless of which enum backs it; see
 * `SimpleLibrarySection.tsx`/`RoleLibrarySection.tsx`/
 * `StructureSection.tsx`). Echoing the raw enum value here undid that:
 * a user who clicks "Retire this skill" and then reads "deprecated" on
 * the badge has no way to know those are the same fact. Neither
 * `docs/UX_ARCHITECTURE.md` nor `docs/FRONTEND.md` ever establishes
 * "deprecated" as user-facing vocabulary — it is a schema/API detail
 * only, unaffected by this display-only normalisation.
 */
export function StatusTag({ status }: { status: string }) {
  if (status === 'active') return null;
  return <span className={styles.statusTag}>{status === 'deprecated' ? 'retired' : status}</span>;
}
